<template>
  <div>
    <CommonTabs
      v-if="isMobile"
      :list="contentTypes"
      :initial-tab-index="activeTabIndex"
      is-collection
      class="mb-8"
      @on-change="handleActiveTab"
    />
    <div v-else class="my-12 flex justify-between">
      <CommonTabs
        :list="contentTypes"
        :initial-tab-index="activeTabIndex"
        is-collection
        @on-change="handleActiveTab"
      />
      <TeacherTileToggler
        class="ml-10 hidden h-fit lg:flex"
        :active-tile="activeIndexTile"
        :icons="groupList"
        @change-group="handleActiveTile"
      />
    </div>
    <div
      v-if="sortedCollection.length"
      class="wrap-group"
      :class="[
        groupClass,
        {
          'rounded-xl border-b border-[#F3F3F3] shadow-drop-2':
            groupClass !== 'tile',
        },
        { '-m-2.5': groupClass === 'tile' },
      ]"
    >
      <template
        v-for="(card, index) in sortedCollection"
        :key="`${card.id}_${index}`"
      >
        <Component
          :is="card?.outer_link || card?.url ? 'a' : NuxtLink"
          v-bind="
            card?.outer_link || card?.url
              ? { href: getUrl(card) }
              : { to: getUrl(card) }
          "
          :target="card?.outer_link && '_blank'"
          class="group-card"
          :class="{
            'border-b border-[#F3F3F3]': groupClass !== 'tile',
          }"
        >
          <MainPageCardsReworkedTeacherContentCard
            v-if="groupClass === 'tile'"
            :data="card"
            disable-announce
          />
          <MainPageCardsReworkedTeacherContentList v-else :data="card" />
        </Component>
      </template>
    </div>
  </div>
</template>

<script setup>
const NuxtLink = resolveComponent('NuxtLink');

const props = defineProps({
  highlight: {
    type: Object,
    default: () => ({}),
  },
  collection: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:collection']);

const route = useRoute();
const router = useRouter();
const { isMobile, getScreenBreakpoint } = useBreakpoint();

const activeTab = ref(null);

const model = computed(
  () => activeTab.value?.model ?? route.query.content ?? 'region_route',
);

const { itemBySlug } = useCollectionItemBySlug(route.params?.paragraph, model);

const data = computed(() => {
  return itemBySlug?.data?.value;
});

const activeTile = computed(() => {
  return route.query.group || 'tile';
});

const contentTypes = computed(() => {
  return props.highlight?.content_types
    ?.filter(({ model }) =>
      ['region_article', 'region_heraldic', 'region_route'].includes(model),
    )
    .sort((a, b) => (a.model > b.model ? 1 : -1));
});

const activeIndexTile = computed(() => {
  return groupList.value?.findIndex((g) => g === activeTile.value);
});

const groupList = computed(() => {
  return ['tile', 'list'];
});

const sortedCollection = computed(() => {
  if (!props.collection) return [];
  return (
    props.collection?.filter(
      ({ model_type: modelType, model_subtype: modelSubtype }) => {
        const itemModel = [
          'region_route',
          'region_heraldic',
          'region_article',
        ].includes(modelSubtype)
          ? modelSubtype
          : modelType;
        return activeTab.value?.model?.includes(itemModel);
      },
    ) || []
  );
});

const groupClass = computed(() => {
  if (['xs', 'sm', 'md'].includes(getScreenBreakpoint.value)) return 'tile';
  return groupList.value[activeIndexTile.value];
});

const activeTabIndex = computed(() => {
  if (!route.query?.content) return contentTypes.value?.[0]?.count ? 0 : null;
  const idx = contentTypes.value?.findIndex(({ model }) => {
    return model === route.query.content;
  });

  return idx !== -1 ? idx : contentTypes.value?.[0]?.count ? 0 : null;
});

/** watch */
watch(
  data,
  () => {
    if (!data.value || data.value.length === 0) return;
    if (props.collection.some(({ id }) => id === data.value[0].id)) return;
    emit('update:collection', data.value);
  },
  { immediate: true },
);

/** methods */
const handleActiveTab = (tab) => {
  if (tab?.model && route.query.content !== tab?.model) {
    router.replace({
      query: {
        ...route.query,
        content: tab.model,
      },
    });
  }

  activeTab.value = tab;
};

const handleActiveTile = (idx) => {
  if (idx === activeIndexTile.value) return;
  router.replace({
    query: {
      ...route.query,
      group: groupList.value[idx],
    },
  });
};

const getUrl = (card) => {
  return (
    card?.outer_link ??
    card?.url ??
    `/destination/${route.params.paragraph}/${card.model_subtype}/${
      card.slug
    }?content=${model.value}${modernIsGroup(groupClass.value)}`
  );
};

/**mounted */
onMounted(() => {
  if (route.query?.content) {
    const queryTab = contentTypes.value?.find(
      ({ model }) => model === route.query.content,
    );
    handleActiveTab(queryTab);
  } else {
    handleActiveTab(contentTypes.value?.[0]);
  }
});
</script>

<style lang="scss">
.wrap-group {
  @apply flex flex-wrap;

  .group-card {
    @apply w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/5 p-2.5;
  }

  &.list {
    @apply flex-col;

    .group-card {
      @apply w-full;
    }
  }
}
</style>
